import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Edit, Eye, EyeOff, Loader2, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { agentAPI } from '@/lib/api';
import { formatDate, isKRRole, isAccountingRole, isAgencyPrincipal, formatPhone } from '@/lib/utils';

/**
 * AgentInformation — read-only view of the identity fields captured at
 * agent creation (see `AgentsPage` add-agent modal + `EditAgentDialog`).
 *
 * Sections mirror the create/edit form 1-to-1 so operators recognise them:
 *   1. Basic — NPN · Agency · First Name · Last Name · Email · Phone
 *   2. Personal — Date of Birth · SSN Last 4 · Full SSN (reveal)
 *   3. Management — Manager / Development Leader
 *   4. Address — Line 1 · Line 2 · City · State · ZIP
 *
 * Editing is delegated to the existing `EditAgentDialog` via the `onEdit`
 * callback so we never fork the write path.
 */
const Row = ({ label, value, testId, mono = false, children }) => (
  <div className="flex flex-col gap-1 min-w-0" data-testid={testId}>
    <div
      className="text-[10px] font-semibold uppercase tracking-wider"
      style={{ color: 'var(--kr-subtle)' }}
    >
      {label}
    </div>
    <div
      className={`text-sm break-words ${mono ? 'font-mono' : 'font-medium'}`}
      style={{ color: 'var(--kr-navy-900)' }}
    >
      {children ?? (value || <span className="text-slate-400 font-normal">—</span>)}
    </div>
  </div>
);

const Section = ({ title, children }) => (
  <div>
    <h4
      className="text-xs font-semibold uppercase tracking-wide mb-3"
      style={{ color: 'var(--kr-navy-700)' }}
    >
      {title}
    </h4>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {children}
    </div>
  </div>
);

export const AgentInformation = ({
  agent, agency, user, onEdit,
  // Phase 27.5 — subset the sections rendered so this same block can back
  // multiple accordion panels ("Personal Information" vs "Contact
  // Information"). Undefined = render every section wrapped in a Card
  // (legacy single-card layout, unchanged for other callers).
  sections,
  bare = false,
}) => {
  const [ssnRevealed, setSsnRevealed] = useState(null); // formatted SSN or null
  const [revealing, setRevealing] = useState(false);

  if (!agent) return null;

  const showAll = !sections || sections.length === 0;
  const show = (name) => showAll || sections.includes(name);

  // Anyone with an Edit right in the existing model can trigger the edit
  // dialog. Agents may edit their own record (already enforced backend-side).
  const canEdit =
    isKRRole(user) ||
    isAccountingRole(user) ||
    isAgencyPrincipal(user) ||
    user?.agent_npn === agent.npn;

  // Full SSN reveal — the backend audits + gates by role/self-service.
  // The button is only shown to roles that the backend will actually
  // authorize (KR / Agency Principal / self).
  const canAttemptReveal =
    !!agent.has_full_ssn &&
    (isKRRole(user) || isAgencyPrincipal(user) || user?.agent_npn === agent.npn);

  const handleReveal = async () => {
    if (ssnRevealed) {
      setSsnRevealed(null);
      return;
    }
    setRevealing(true);
    try {
      const res = await agentAPI.revealSSN(agent.npn);
      setSsnRevealed(res.data?.ssn || res.data?.ssn_full || res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to reveal SSN');
    } finally {
      setRevealing(false);
    }
  };

  const body = (
    <div className="space-y-6">
      {!bare && (
        <div className="flex items-center justify-between">
          <h3
            className="text-base font-heading font-semibold"
            style={{ color: 'var(--kr-navy-900)' }}
          >
            Agent Information
          </h3>
          {canEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={onEdit}
              data-testid="agent-info-edit-btn"
            >
              <Edit size={14} className="mr-1" /> Edit
            </Button>
          )}
        </div>
      )}
      {bare && canEdit && (
        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={onEdit}
            data-testid="agent-info-edit-btn"
          >
            <Edit size={14} className="mr-1" /> Edit
          </Button>
        </div>
      )}

      {/* ── Basic ─────────────────────────────────────────────── */}
      {show('basic') && (
        <Section title="Basic Information">
          <Row label="NPN" value={agent.npn} mono testId="agent-info-npn" />
          <Row label="Agency" testId="agent-info-agency">
            {agency?.agency_id ? (
              <Link
                to={`/agencies/${agency.agency_id}`}
                className="hover:underline"
                style={{ color: 'var(--kr-navy-700)' }}
              >
                {agency.agency_name}
              </Link>
            ) : (
              <span className="text-slate-400 font-normal">—</span>
            )}
          </Row>
          <Row label="First Name" value={agent.first_name} testId="agent-info-first-name" />
          <Row label="Last Name" value={agent.last_name} testId="agent-info-last-name" />
          <Row label="Email" testId="agent-info-email">
            {agent.email ? (
              <a
                href={`mailto:${agent.email}`}
                className="hover:underline break-all"
                style={{ color: 'var(--kr-navy-700)' }}
              >
                {agent.email}
              </a>
            ) : (
              <span className="text-slate-400 font-normal">—</span>
            )}
          </Row>
          <Row label="Phone" testId="agent-info-phone">
            {agent.phone ? (
              <a
                href={`tel:${agent.phone}`}
                className="hover:underline"
                style={{ color: 'var(--kr-navy-700)' }}
              >
                {formatPhone(agent.phone)}
              </a>
            ) : (
              <span className="text-slate-400 font-normal">—</span>
            )}
          </Row>
          <Row
            label="Preferred Language"
            value={agent.preferred_language === 'es' ? 'Español' : 'English'}
            testId="agent-info-preferred-language"
          />
        </Section>
      )}

      {/* ── Personal ──────────────────────────────────────────── */}
      {show('personal') && (
        <Section title="Personal Information">
          <Row
            label="Date of Birth"
            value={agent.date_of_birth ? formatDate(agent.date_of_birth) : null}
            testId="agent-info-dob"
          />
          <Row
            label="SSN (Last 4)"
            testId="agent-info-ssn-last4"
          >
            {agent.ssn_last4 ? (
              <span className="font-mono">•••-••-{agent.ssn_last4}</span>
            ) : (
              <span className="text-slate-400 font-normal">—</span>
            )}
          </Row>
          <Row label="Full SSN" testId="agent-info-ssn-full">
            <div className="flex items-center gap-2">
              {ssnRevealed ? (
                <span className="font-mono">{ssnRevealed}</span>
              ) : agent.has_full_ssn ? (
                <span className="font-mono text-slate-500">•••-••-••••</span>
              ) : (
                <span className="text-slate-400 font-normal">Not on file</span>
              )}
              {canAttemptReveal && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={handleReveal}
                  disabled={revealing}
                  data-testid="agent-info-reveal-ssn-btn"
                >
                  {revealing ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : ssnRevealed ? (
                    <><EyeOff size={12} className="mr-1" /> Hide</>
                  ) : (
                    <><Eye size={12} className="mr-1" /> Reveal</>
                  )}
                </Button>
              )}
              {agent.has_full_ssn && !canAttemptReveal && (
                <Shield size={12} className="text-amber-600" />
              )}
            </div>
          </Row>
        </Section>
      )}

      {/* ── Management ────────────────────────────────────────── */}
      {show('management') && (
        <Section title="Management">
          <Row
            label="Manager / Development Leader"
            value={agent.manager_name}
            testId="agent-info-manager"
          />
          <Row label="Puede enviar SMS a Leads" testId="agent-info-can-sms-leads">
            {agent.can_sms_leads ? (
              <span className="inline-flex items-center gap-1 text-emerald-700 font-medium">
                Sí
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-slate-500">
                No
              </span>
            )}
          </Row>
        </Section>
      )}

      {/* ── Address ───────────────────────────────────────────── */}
      {show('address') && (
        <Section title="Address">
          <Row
            label="Address Line 1"
            value={agent.address_line1}
            testId="agent-info-address1"
          />
          <Row
            label="Address Line 2"
            value={agent.address_line2}
            testId="agent-info-address2"
          />
          <Row label="City" value={agent.city} testId="agent-info-city" />
          <Row label="State" value={agent.state} testId="agent-info-state" />
          <Row label="ZIP Code" value={agent.zip} testId="agent-info-zip" mono />
        </Section>
      )}
    </div>
  );

  if (bare) return <div data-testid="agent-information-card">{body}</div>;

  return (
    <Card className="mb-4" data-testid="agent-information-card">
      <CardContent className="pt-6">{body}</CardContent>
    </Card>
  );
};

export default AgentInformation;
